import { Back, Power1, TimelineMax, TweenMax } from 'gsap';
import { Container } from 'pixi.js';
import { GameAudio } from '../../Config/Audio';
import { getGameSize, IGameSizeInfo } from '../../Config/Levels';
import Core from '../../Core/Core';
import { gameConfig } from '../../index';
import { HudActions, HudEmitter } from '../../Managers/Hud/HudEmitter';
import { Popup } from '../../Managers/Popups';
import SoundManager from '../../Sound/SoundManager';
import { IRewardItem } from '../../State/reducers/score';
import DateUtils from '../../Utils/DateUtils';
import { createOutlineText } from '../../Utils/Utils';
import { Confetti } from '../Animations/Confetti';
import { SkippableAnimation } from '../Animations/SkippableAnimation';
import { buttonIdleAnimation } from '../Animations/UIAnimations';
import Rectangle = PIXI.Rectangle;
import { ITweenable } from '../PopUps/ITweenable';
import { PopupHeader } from '../PopUps/PopupHeader';
import { JackpotShareableOverlay } from '../PopUps/Social/ShareableMoments/JackpotShareableOverlay';
import { ShareableOverlay } from '../PopUps/Social/ShareableMoments/ShareableOverlay';
import { RewardGroup } from '../PopUps/Win/RewardGroup';
import { AdButton } from '../Shared/Buttons/AdButton';
import { TextButton } from '../Shared/Buttons/TextButton';
import { UIButton } from '../Shared/Buttons/UIButton';
import { HighlightEffect } from '../Shared/HighlightEffect';
import { ScreenBlur } from '../Shared/ScreenBlur';
import { PrizeWheelLights } from './PrizeWheelLights';
// import { waitForMs } from "../../Utils/AsyncUtils";

/**
 * cooldown of the prizewheel before the player can do a free spin again
 */
export const COOLDOWNPRIZEWHEEL = DateUtils.hoursToMilliseconds(12);

/**
 * Prize Wheel Popup
 * THe player can spin the wheel to win prizes
 * Resets every 24 hours
 * Players can also watch an add to spin the wheel again
 */
export class PrizeWheelPopup extends PIXI.Container implements ITweenable {
    public spinBtn: TextButton;
    public ts: number;
    private wheelLights: PrizeWheelLights;
    private arrow: PIXI.Sprite;
    private bg: ScreenBlur;
    private header: PopupHeader;
    private prizeWheel: Container;
    private adBtn: AdButton;
    private disclamerText: PIXI.Text;
    private oldRotation: number = 0;
    private soundPlayed: boolean = false;
    private confetti: Confetti;
    private wheelContainer: Container;
    private shareableOverlay: ShareableOverlay;

    constructor(ts: number) {
        super();
        const gameSize = getGameSize();
        const gameConf = gameConfig;

        this.bg = new ScreenBlur();
        this.bg.interactive = true;
        this.header = new PopupHeader('prizewheel', Popup.PRIZEWHEEL);

        this.ts = ts;

        this.prizeWheel = new Container();

        this.prizeWheel.x = gameSize.gameWidth / 2;
        this.prizeWheel.y = gameSize.gameHeight * 0.45;
        this.prizeWheel.pivot.x = this.prizeWheel.width / 2;
        this.prizeWheel.pivot.y = this.prizeWheel.height / 2;

        const prizeWheel = new PIXI.Sprite(PIXI.utils.TextureCache['mapui/prize-wheel']);
        prizeWheel.anchor.set(0.5);
        this.prizeWheel.addChild(prizeWheel);

        const wheelLights = new PrizeWheelLights();
        wheelLights.anchor.set(0.5);
        wheelLights.x = gameSize.gameWidth / 2;
        wheelLights.y = this.prizeWheel.y;
        this.wheelLights = wheelLights;

        const disclamer = createOutlineText(
            Core.local.getText('disclaimerprizewheelfree'),
            new Rectangle(0, 0, gameConf.game_width, gameConf.game_height),
            'Baloo',
            30,
            30
        );
        disclamer.anchor.set(0.5);
        disclamer.x = gameSize.gameWidth / 2;
        disclamer.y = this.prizeWheel.y + this.prizeWheel.height / 1.75;
        this.disclamerText = disclamer;

        const arrow = new PIXI.Sprite(PIXI.utils.TextureCache['mapui/prize-wheel-needle']);
        arrow.anchor.set(0.5, 0.3);
        arrow.x = gameSize.gameWidth / 2;
        arrow.y = this.prizeWheel.y - prizeWheel.height * 0.45;
        this.arrow = arrow;

        const spinBtn = new TextButton({
            width: 250,
            height: 110,
            btnText: Core.local.getText('spin'),
        });
        spinBtn.x = gameSize.gameWidth / 2;
        spinBtn.y = disclamer.y + disclamer.height / 2 + spinBtn.height / 2;
        spinBtn.on('trigger', () => {
            this.handleBtn('spin', spinBtn);
        });
        this.spinBtn = spinBtn;

        const adBtn = new AdButton(spinBtn.width, spinBtn.height, Core.local.getText('spin'));
        adBtn.alpha = 0;
        adBtn.x = spinBtn.x;
        adBtn.y = spinBtn.y;
        adBtn.on('trigger', () => {
            this.handleBtn('watch', adBtn);
        });
        this.adBtn = adBtn;
        this.adBtn.interactive = false;
        this.adBtn.buttonMode = false;
        if (ts > 0) {
            this.spinBtn.interactive = false;
            this.spinBtn.buttonMode = false;
            this.spinBtn.alpha = 0;
            if (Core.sdk.ads.getRewardedEnabled()) {
                this.tweenButton(this.adBtn, true);
                buttonIdleAnimation(this.adBtn, 3);
                this.changeDisclamerText(Core.local.getText('disclaimerprizewheeladd'));
            } else {
                this.changeDisclamerText(Core.local.getText('disclaimerprizewheelnoadd'));
            }
        } else {
            buttonIdleAnimation(this.spinBtn, 3);
        }
        this.wheelContainer = new Container();
        this.wheelContainer.addChild(
            disclamer,
            this.prizeWheel,
            wheelLights,
            arrow,
            spinBtn,
            adBtn
        );
        this.wheelContainer.alpha = 0;
        this.addChild(this.bg, this.wheelContainer, this.header);
        this.setLightMode(true).catch(err => {
            console.log(['error with prize wheel update', err]);
        });
    }

    /**
     * spins the wheel to the reward
     * @param angle the angle on the wheel where the reward is
     */
    public spinWheel(index: number, reward: IRewardItem) {
        const angle = (360 / 8) * index;
        this.tweenButton(this.header.closeButton, false);

        const rnd = this.int(8, 18) * this.sign();
        const startAngle = 0;
        const endAngle = ((angle + 360 * 8 + rnd) / 180) * Math.PI;
        this.prizeWheel.rotation = 0;
        new TimelineMax()
            .to(this.prizeWheel, 1, {
                ease: Power1.easeIn,
                rotation: startAngle + Math.PI * 4,
                onUpdate: () => {
                    this.spinHit(this.prizeWheel.rotation);
                },
            })
            .to(this.prizeWheel, 7, {
                ease: Power1.easeOut,
                rotation: endAngle,
                onUpdate: () => {
                    this.spinHit(this.prizeWheel.rotation);
                },
            })
            .eventCallback('onComplete', () => {
                this.setLightMode(false).catch(err => {
                    console.log(['error with prize wheel update 2', err]);
                });
                this.tweenButton(this.spinBtn, false);
                this.tweenButton(this.adBtn, false);
                this.tweenButton(this.header.closeButton, true);

                if (index === 3) {
                    TweenMax.to(this.wheelContainer, 0.3, { alpha: 0.5 });
                    this.shareableOverlay = new JackpotShareableOverlay();
                    this.addChild(this.shareableOverlay);
                    this.shareableOverlay.tweenIn();
                    return;
                }

                this.showReward(reward).catch(err => {
                    console.log(['error with prize wheel show reward', err]);
                });
                this.confetti = new Confetti();
                this.addChild(this.confetti);
            });
    }

    public int(min: number, max: number): number {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    /**
     * changes the description text shown above the wheel
     * @param s the new text
     */
    public changeDisclamerText(s: string) {
        this.disclamerText.text = s;
    }

    public async tweenIn(): Promise<void> {
        this.header.tweenIn();
        TweenMax.to(this.wheelContainer, 0.3, {
            alpha: 1,
        });
    }

    public async tweenOut(): Promise<void> {
        this.header.tweenOut();
        if (this.shareableOverlay) {
            await this.shareableOverlay.tweenOut();
        }
        TweenMax.to(this, 0.3, {
            alpha: 0,
            onComplete: () => {
                this.cleanUp();
            },
        });
    }

    /**
     * shows the reward the player earned from the prize wheel
     * called once the prize wheel stopped spinning
     * @param reward the reward the player gets
     */
    public async showReward(reward: IRewardItem) {
        SoundManager.playSound(GameAudio.PRIZEWHEEL_REWARD);
        const rewardGroup = new RewardGroup(reward.id, reward.val);
        rewardGroup.anchor.set(0.5);
        rewardGroup.scale.set(0);
        rewardGroup.x = this.prizeWheel.x;
        rewardGroup.y = this.prizeWheel.y;

        const highlight = new HighlightEffect({ scale: 1.1 });
        highlight.anchor.set(0.5);
        highlight.x = rewardGroup.x;
        highlight.y = rewardGroup.y;
        //@ts-ignore
        this.addChild(highlight, rewardGroup);
        highlight.appear();

        const rewardGroupScale = 1.5;
        const animation = new SkippableAnimation();
        animation.add(
            TweenMax.to(rewardGroup.scale, 0.5, {
                x: rewardGroupScale,
                y: rewardGroupScale,
                ease: Back.easeOut.config(3),
            })
        );

        animation.addDelay(500);
        animation.add(() => {
            if (Core.sdk.ads.getRewardedEnabled()) {
                this.tweenButton(this.adBtn, true);
                buttonIdleAnimation(this.adBtn, 3);
                this.changeDisclamerText(Core.local.getText('disclaimerprizewheeladd'));
            } else {
                this.changeDisclamerText(Core.local.getText('disclaimerprizewheelnoadd'));
            }
        });

        animation.addDelay(2000);
        animation.add(
            TweenMax.to([rewardGroup.scale, highlight.scale], 0.4, {
                x: 0,
                y: 0,
            })
        );
        animation.add(() => {
            rewardGroup.destroy();
            highlight.cleanUp();
            this.setLightMode(true).catch(err => {
                console.log(['error with prize wheel update 3', err]);
            });
        });
        await animation.play();
    }

    /**
     * changes the alpha of the button and sets its interactive and its button mode
     * @param btn the button to change the values of
     * @param state true: button active; false: button inactive
     */
    public tweenButton(btn: UIButton, state: boolean) {
        TweenMax.killTweensOf(btn);
        TweenMax.killTweensOf(btn.scale);
        btn.interactive = state;
        btn.buttonMode = state;
        const a = state ? 1 : 0;
        TweenMax.to(btn, 0.2, {
            alpha: a,
        });
    }

    public cleanUp() {
        TweenMax.killTweensOf(this.wheelLights);
        TweenMax.killTweensOf(this.header);
        if (this.confetti) {
            this.confetti.cleanUp();
        }
        this.destroy({ children: true });
    }

    public resize(sizeInfo: IGameSizeInfo) {
        this.prizeWheel.x = sizeInfo.gameWidth / 2;
        this.wheelLights.x = sizeInfo.gameWidth / 2;
        this.spinBtn.x = sizeInfo.gameWidth / 2;
        this.adBtn.x = sizeInfo.gameWidth / 2;
        this.disclamerText.x = sizeInfo.gameWidth / 2;
        this.arrow.x = sizeInfo.gameWidth / 2;
        this.header.x = sizeInfo.gameWidth / 2;
    }

    private sign() {
        return this.int(0, 1) * 2 - 1;
    }

    /**
     * updates the lights
     * slow update true: 2 sec for light switch
     * slow update false: 0.2 sec for light switch
     * @param fast start state
     */
    private async setLightMode(fast: boolean) {
        TweenMax.killTweensOf(this.wheelLights);
        new TimelineMax({ repeat: -1 })
            .to(this.wheelLights, fast ? 2 : 0.2, {
                onStart: () => {
                    this.wheelLights.setLight(true);
                },
            })
            .to(this.wheelLights, fast ? 2 : 0.2, {
                onStart: () => {
                    this.wheelLights.setLight(false);
                },
            });
    }

    /**
     * handle button click
     * @param s the functionality of the button pressed
     * @param btn the button pressed
     */
    private handleBtn(s: string, btn: UIButton) {
        switch (s) {
            case 'spin':
                this.tweenButton(this.spinBtn, false);
                break;
            case 'watch':
                this.tweenButton(this.adBtn, false);
                break;
        }

        HudEmitter.instance.emit(HudActions.PRIZE_WHEEL_ACTION, s);
    }

    private spinHit(newRotation: number) {
        let stop = newRotation * (180 / Math.PI);
        while (stop > 360) {
            stop -= 360;
        }
        let count = 0;
        let start = this.oldRotation;
        for (start; start <= stop; start++) {
            if (
                start === 23 ||
                start === 68 ||
                start === 113 ||
                start === 158 ||
                start === 202 ||
                start === 248 ||
                start === 292 ||
                start === 338
            ) {
                count++;
            }
        }
        if (count > 0) {
            this.animateArrow();
        }
        this.oldRotation = Math.round(stop);
    }

    private animateArrow() {
        TweenMax.to(this.arrow, 0.1, {
            rotation: -0.5,
            onComplete: () => {
                TweenMax.to(this.arrow, 0.1, {
                    rotation: 0,
                });
            },
            onStart: () => {
                this.soundPlayed = false;
            },
        });
        if (!this.soundPlayed) {
            SoundManager.playSound(GameAudio.PRIZEWHEEL_SPIN);
            this.soundPlayed = true;
        }
    }
}
